import {
  type KycResponse,
  type IdentitiesKycStatus,
  type IdentitiesKyc,
  KycCurrentStatus,
  KycProcessType,
  KycProcessStatus,
} from '@repo/api-kit/queries/due-diligence/kyc';

export interface LegacyKycInput extends IdentitiesKyc {
  // From membership aggregate — determines activeProcess presence and status
  kycRequired?: boolean;
  kycSubmitted?: boolean;
  personalInfoProvided?: boolean;
}

const KYC_STATUS_TO_CURRENT_STATUS: Partial<
  Record<IdentitiesKycStatus, KycResponse['kycCurrentStatus']>
> = {
  accepted: KycCurrentStatus.accepted,
  pending: KycCurrentStatus.pending,
  refused: KycCurrentStatus.refused,
  // In the old API, a past-due re-kyc user (restricted/suspended) has a non-accepted kycStatus (e.g. 'waiting_document').
  // The new API returns `pending` in this case, so we normalize it here.
  waiting_document: KycCurrentStatus.pending,
};

function resolveKycCurrentStatus(
  legacyKycStatus: LegacyKycInput['kycStatus']
): KycResponse['kycCurrentStatus'] {
  return (
    KYC_STATUS_TO_CURRENT_STATUS[legacyKycStatus] ?? KycCurrentStatus.KYC_CURRENT_STATUS_UNSPECIFIED
  );
}

function resolveProcessStatus({
  personalInfoProvided,
  kycSubmitted,
  kycStatus,
}: LegacyKycInput): NonNullable<KycResponse['activeProcess']>['processStatus'] {
  if (personalInfoProvided === false) return KycProcessStatus.pending_personal_info;
  if (kycSubmitted === true) return KycProcessStatus.submitted;
  if (kycStatus === 'waiting_document') return KycProcessStatus.pending_user_action;
  return KycProcessStatus.pending_liveness;
}

function resolveActiveProcess(input: LegacyKycInput): KycResponse['activeProcess'] {
  if (!input.kycRequired && !input.isReKycRequired && !input.isRenewalPoiRequired) return null;

  const baseProcess = {
    provider: null,
    providerDetails: null,
    signatureRequired: false,
  } as const;

  const processStatus = resolveProcessStatus(input);

  if (input.isReKycRequired) {
    return {
      ...baseProcess,
      type: KycProcessType.re_kyc,
      processStatus,
      dueDate: input.reKycDueDate ? new Date(input.reKycDueDate) : null,
    };
  }

  if (input.isRenewalPoiRequired) {
    return {
      ...baseProcess,
      type: KycProcessType.poi_renewal,
      processStatus,
      dueDate: null,
    };
  }

  return {
    ...baseProcess,
    type: KycProcessType.initial,
    processStatus,
    dueDate: null,
  };
}

export function mapKycLegacyToAcceptance(input: LegacyKycInput): KycResponse {
  return {
    kycCurrentStatus: resolveKycCurrentStatus(input.kycStatus),
    activeProcess: resolveActiveProcess(input),
  };
}
